import { useLocation } from "@tanstack/react-router";
import type React from "react";
import { type PropsWithChildren, useEffect, useRef, useState } from "react";

import DropdownMenu from "../../organisms/DropDownMenu/DropdownMenu";
import NavbarAnimatedText from "./NavbarAnimatedText";
import NavbarBackgroundIndicator from "./NavbarBackgroundIndicator";
import NavbarItemIcon from "./NavbarItemIcon";

type Props = {
  active: boolean;
  Icon: React.FC;
  label: string;
  setActiveIndex: () => void;
  onMouseLeave?: () => void;
  dropdownMenuContentClassName?: string;
};

const NavbarDropdownItem = ({
  active,
  Icon,
  label,
  children,
  setActiveIndex,
  onMouseLeave,
  dropdownMenuContentClassName,
}: PropsWithChildren<Props>) => {
  const { pathname } = useLocation();
  const [open, setOpenState] = useState(false);
  const isOpenRef = useRef(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const setOpen = (value: boolean) => {
    isOpenRef.current = value;
    setOpenState(value);
  };

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const cancelClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      onMouseLeave?.();
    }, 100);
  };

  return (
    <DropdownMenu
      open={open}
      // onOpenChange={setOpen}
      // onClick={() => setOpen(true)}
      modal={false}
      triggerContainerClassName="h-14 w-[14%] cursor-pointer text-white hover:text-gray-300"
      className={dropdownMenuContentClassName}
      onContentMouseEnter={cancelClose}
      onContentMouseLeave={scheduleClose}
      trigger={
        <div
          onPointerDown={(e) => {
            // Hover-only menu: block Radix's pointerdown toggle so clicking
            // the trigger doesn't close/reopen it.
            e.preventDefault();
          }}
          onMouseEnter={() => {
            cancelClose();
            setActiveIndex();
            if (!isOpenRef.current) {
              setOpen(true);
            }
          }}
          onMouseLeave={(e) => {
            if (contentRef.current?.contains(e.relatedTarget as Node)) return;
            scheduleClose();
          }}
          className="relative"
        >
          <NavbarItemIcon active={active}>
            <Icon />
          </NavbarItemIcon>
          {active && (
            <>
              <NavbarBackgroundIndicator />
              <NavbarAnimatedText label={label} />
            </>
          )}
        </div>
      }
    >
      <div ref={contentRef}>{children}</div>
    </DropdownMenu>
  );
};

export default NavbarDropdownItem;
